import { Component, computed, effect, input, InputSignal, output, OutputEmitterRef, signal, Signal, OnInit } from '@angular/core';
import { Experiment, Regimen, User } from "@cureskin/api-client/src/server-api";
import { CsIconModule } from '@components/icon';
import { CommonModule } from '@angular/common';
import { extractRegimenProducts } from 'client/helper/regimen-helper';
import { ApiClientConstant } from '@cureskin/api-client';
import { planName, PlanName, selectedPlanAndVariantIdMap, RegimenService } from '@services/regimen-service';
import { getPlanInfoConfig, PlanInfoItem, planInfoItemId, planInfoImageUrls } from '../choose-plan-info/choose-plan-info.constants';
import { EventLoggerService } from '@services/event-logger-service';
import { AppConfig } from 'client/app/app.config';

@Component({
  selector: 'regimen-value-proposition-v2',
  imports: [CsIconModule, CommonModule],
  templateUrl: './regimen-value-proposition-v2.component.html',
})
export class RegimenValuePropositionV2Component implements OnInit {
  regimenUpdateTrigger: InputSignal<Regimen> = input();
  regimenChangeSignal: InputSignal<Regimen> = input();

  //readonly input signal
  regimenData = signal<Regimen | null>(null);

  user: InputSignal<User> = input();
  backgroundColor: InputSignal<string> = input();
  allocatedDoctorImage: InputSignal<string> = input();
  moneyBackGuaranteeExperiment: InputSignal<Experiment | null> = input();
  patientName: Signal<string> = computed(() => this.user()?.get('PatientName')?.split(' ')[0] || '');
  regimenProductsLength: Signal<number> = computed(() => extractRegimenProducts(this.regimenData() || {})?.length || 0);
  readMoreClick: OutputEmitterRef<void> = output();
  readonly ApiClientConstant = ApiClientConstant;
  planInfoItemId = planInfoItemId;

  // Cache the config to avoid creating new objects on every access
  private _planInfoConfigCache: ReturnType<typeof getPlanInfoConfig> | null = null;
  private _lastExperimentCacheKey: string | null = null;

  // Computed signal to get current plan info config based on variantId
  currentPlanInfo = computed(() => {
    const regimenData = this.regimenData();
    const variantId = (regimenData as any)?.variantId;
    const selectedVariantId = (regimenData as any)?.selectedVariantId || variantId;
    const regimenClass = (regimenData as any)?.class;
    const experiment = this.moneyBackGuaranteeExperiment();
    const currentPlan = Object.values(planName).find(
      (plan) => selectedPlanAndVariantIdMap[plan] === selectedVariantId
    );
    const cacheKey = `${regimenClass}-${experiment?.objectId || 'none'}-${selectedVariantId}`;
    if (!this._planInfoConfigCache || this._lastExperimentCacheKey !== cacheKey) {
      const enabledPlans = this.getEnabledPlansForMoneyBackGuarantee(regimenClass);
      this._planInfoConfigCache = getPlanInfoConfig(true, enabledPlans);
      this._lastExperimentCacheKey = cacheKey;
    }
    return this._planInfoConfigCache[currentPlan || planName.advance];
  });

  private getEnabledPlansForMoneyBackGuarantee(regimenClass: string): PlanName[] {
    const experiment = this.moneyBackGuaranteeExperiment();
    return this.regimenService.getEnabledPlansForMoneyBackGuarantee(experiment, regimenClass, this.user());
  }

  constructor(
    private readonly eventLoggerService: EventLoggerService,
    private readonly appConfig: AppConfig,
    private readonly regimenService: RegimenService,
  ) {
    effect(() => {
      const updatedRegimen = this.regimenUpdateTrigger();
      if (updatedRegimen) {
        this.regimenData.set(updatedRegimen);
     }
    });
  }

  ngOnInit(): void {
    this.eventLoggerService.cleverTapEvent('pageOpen', JSON.stringify({ name: 'treatment-plan-info', regimenClass: this.regimenData()?.class }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  readMoreAboutServices(): void {
    this.readMoreClick.emit();
  }

  getItemImage(item: PlanInfoItem): string {
    if (item.id === planInfoItemId.PRODUCTS && this.regimenData()?.regimenCoverImage) {
      return this.regimenData()?.regimenCoverImage;
    }
    if ((item.id === planInfoItemId.DEDICATED_DOCTOR || item.id === planInfoItemId.ONE_ON_ONE_DOCTOR) && this.allocatedDoctorImage()) {
      return this.allocatedDoctorImage();
    }
    switch (item.id) {
      case planInfoItemId.MONTHLY_CHECKUPS:
        return planInfoImageUrls.CHECKUP_REPORT_SQUARE;
      case planInfoItemId.NUTRITIONIST_SESSION:
      case planInfoItemId.NUTRITIONIST_SESSIONS:
        return planInfoImageUrls.CALL_ICONS_SQUARE;
      case planInfoItemId.DIET_PLAN:
        return planInfoImageUrls.DIET_PLAN_SQUARE;
      default:
        return item.imageUrl;
    }
  }
}
